import router from '@/router'

export const state = {
    leftPanel: {
        expanded: true,
        transitioning: false,
    },
    rightPanel: {
        title: '',
        transitioning: false,
    },
    help: {
        isOpen: false,
        content: ''
    },
    createClientModal: {
        isOpen: false,
    },
    debugMode: false,
};

const environments = [
    "development",
    "staging",
    "production"
]

const navHiddenPaths = [
    '/clients/bulkUpdate',
    '/clients/bulkEdit',
    '/clients/bulkDelete',
    '/clients/bulkEdit/summary',
    '/clients/bulkDelete/summary',
];

/*
    Actions hook up to the interface, but they do not touch the store data directly.
    They can be rather complicated and will generally lean on one or more mutations.
    Actions are also the communication layer with external sources (API).

    Actions come with access to four contexts ({ state, getters, commit, dispatch })
        state = direct data access... avoid use
        getters = indirect data access... this is good
        commit = call a mutation
        dispatch = for us, dispatch will almost certainly be an axios api call
*/
export const actions = {
    async navPanelTransitioning({ commit }) {
        commit("SET_NAV_PANEL_TRANSITIONING", true);
        await new Promise(resolve => setTimeout(resolve, 2100));
        commit("SET_NAV_PANEL_TRANSITIONING", false);
    },
    async rightPanelTransitioning({ commit }) {
        commit("SET_RIGHT_PANEL_TRANSITIONING", true);
        await new Promise(resolve => setTimeout(resolve, 2100));
        commit("SET_RIGHT_PANEL_TRANSITIONING", false);
    },
    /**
     * Toggle the side navigation state
     * @param {*} contexts - commit, getters 
     */
    toggleNav({ commit, dispatch }) {
        dispatch('navPanelTransitioning');
        commit("TOGGLE_NAV");
    },
    /**
     * Open the help panel
     * @param {*} contexts - commit, getters 
     */
    openHelp({ commit, dispatch }, { content }) {
        commit("CLOSE_ACTIVE_RIGHT_PANELS");
        commit("SET_RIGHT_PANEL_TITLE", "Help");
        dispatch('rightPanelTransitioning');
        commit("OPEN_HELP", { content });
    },
    /**
     * Close the right panel
     * @param {*} contexts - commit, getters 
     */
    closeRightPanel({ commit, dispatch, getters }) {
        const activeTabId = getters.getActiveTab.ClientInfoRuleId;
        dispatch('rightPanelTransitioning');
        commit("CLOSE_CHANGE_HISTORY", { ClientInfoRuleId: activeTabId });
        commit("CLOSE_ACTIVE_RIGHT_PANELS");
    },
    /**
     * Open the create client modal
     * @param {*} contexts - commit, getters 
     */
    openCreateClientModal({ commit }) {
        commit("OPEN_CREATE_CLIENT_MODAL");
    },
    /**
     * Close the create client modal
     * @param {*} contexts - commit, getters 
     */
    closeCreateClientModal({ commit }) {
        commit("CLOSE_CREATE_CLIENT_MODAL");
    },
    /**
     * Enable debug mode
     * @param {*} contexts - commit 
     */
    enableDebugMode({ commit }) {
        commit("ENABLE_DEBUG_MODE");
        router.replace('/');
    },
    /**
     * Disable debug mode
     * @param {*} contexts - commit 
     */
    disableDebugMode({ commit }) {
        commit("DISABLE_DEBUG_MODE");
    },
};

/*
    Mutations are methods that act on the state data directly.
    They should be incredibly basic and perform a single task.

    Mutations always take in the current 'state' and a payload.
*/
export const mutations = {
    /**
     * Toggle the side navigation state
     * @param {*} state - current state
     */
    TOGGLE_NAV(state) {
        state.leftPanel.expanded = !state.leftPanel.expanded;
    },
    SET_NAV_PANEL_TRANSITIONING(state, value) {
        state.leftPanel.transitioning = !!value;
    },
    SET_RIGHT_PANEL_TRANSITIONING(state, value) {
        state.rightPanel.transitioning = !!value;
    },
    SET_RIGHT_PANEL_TITLE(state, title) {
        state.rightPanel.title = title;
    },
    /**
     * Open the help panel
     * @param {*} state - current state
     */
    OPEN_HELP(state, { content }) {
        state.help.content = content;
        state.help.isOpen = true;
    },
    /**
     * Close the right panels
     * @param {*} state - current state
     */
    CLOSE_ACTIVE_RIGHT_PANELS(state) {
        state.help.isOpen = false;
    },
    /**
     * Open create client modal
     * @param {*} state - current state
     */
    OPEN_CREATE_CLIENT_MODAL(state) {
        state.createClientModal.isOpen = true;
    },
    /**
     * Close create client modal
     * @param {*} state - current state
     */
    CLOSE_CREATE_CLIENT_MODAL(state) {
        state.createClientModal.isOpen = false;
    },
    ENABLE_DEBUG_MODE(state) {
        state.debugMode = true;
    },
    DISABLE_DEBUG_MODE(state) {
        state.debugMode = false;
    },
};

/*
    Getters are non-cached access points to the state data.
    They allow us to interrogate the data and pull back dynamic subsets.
*/

export const getters = {
    getEnvironment: () => {
        return process.env.VUE_APP_ENV || 'production'
    },
    getNextEnvironment: (state, getters) => {
        const currentEnv = getters.getEnvironment;
        const envIndex = environments.indexOf(currentEnv);
        return environments[envIndex + 1] || undefined;
    },
    /**
     * gets the nav expanded state
     * @param {*} state - current state
     * @returns Bool of nav expansion state
     */
    getNavExpanded: state => state.leftPanel.expanded,
    /**
     * gets the transitioning state of the left panel
     * @param {*} state - current state
     * @returns Bool of transition state
     */
    getNavPanelTransitioning: state => state.leftPanel.transitioning,
    /**
     * gets the hidden state for the side nav
     * @param {*} state - current state
     * @returns Bool of nav hidden state
     */
    getNavHidden: (state, getters, rootState) => {
        return navHiddenPaths.includes(rootState.route.path);
    },
    /**
     * gets the width of the side nav
     * @param {*} state - current state
     * @returns Number in pixels of width
     */
    getSideNavWidth: (state, getters) => {
        if (getters.getNavHidden) return 0;
        return getters.getNavExpanded ? 256 : 85;
    },
    /**
     * gets the change history panel open state
     * @param {*} state - current state
     * @returns Bool of change history panel state
     */
    getChangeHistoryOpen: (state, getters, rootState) => getters.getActiveTab.showChangeHistory && rootState.route.path === '/clients',
    /**
     * gets the client data for the change history
     * @param {*} state - current state
     * @returns Object of client info for open change history panel
     */
    getChangeHistoryClientData: (state, getters) => ({ ClientId: getters.getActiveTab.ClientId, ClientInfoRuleId: getters.getActiveTab.ClientInfoRuleId }),
    /**
     * gets the help panel open state
     * @param {*} state - current state
     * @returns Bool of help panel state
     */
    getHelpOpen: state => state.help.isOpen,
    /**
     * gets the help panel content
     * @param {*} state - current state
     * @returns String of help panel content
     */
    getHelpContent: state => state.help.content,
    /**
     * gets the right panel title
     * @param {*} state - current state
     * @returns String of title
     */
    getRightPanelTitle: state => state.rightPanel.title,
    /**
     * gets the transitioning state of the right panel
     * @param {*} state - current state
     * @returns Bool of transition state
     */
    getRightPanelTransitioning: state => state.rightPanel.transitioning,
    /**
     * gets the right panel panel open state
     * @param {*} state - current state
     * @returns Number in pixels of width
     */
    getRightPanelOpen: (state, getters) => getters.getChangeHistoryOpen || getters.getHelpOpen,
    /**
     * gets the width of the right panel
     * @param {*} state - current state
     * @returns Number in pixels of width
     */
    getRightPanelWidth: (state, getters) => {
        const defaultOpenWidth = 256;
        if (getters.getHelpOpen) return defaultOpenWidth;
        if (getters.getChangeHistoryOpen) return defaultOpenWidth;
        return 0;
    },
    /**
     * gets the width of the left and right panels
     * @param {*} state - current state
     * @returns Number in pixels of width
     */
    getTotalPanelWidth: (state, getters) => getters.getSideNavWidth + getters.getRightPanelWidth,
    /**
    * gets if the create client modal is open
    * @param {*} state - current state
    * @returns Bool of modal state
    */
    getCreateClientModalOpen: state => state.createClientModal.isOpen,
    /**
    * is app in debug mode?
    * @returns Bool of debug mode status
    */
    getDebugMode: state => state.debugMode,
};